import clsx from "clsx";
import { Check, Circle, CircleAlert, Info, TriangleAlert } from "lucide-react";
import { createPluggable } from "pluggable-react";
import { useEffect, useRef } from "react";

import Pressable from "../Pressable/Pressable";
import Tooltip from "../Tooltip/Tooltip";
import styles from "./SteppedProgress.module.scss";
import "../../../styles/focus-ring.scss";

export type SteppedProgressState = "success" | "in-progress" | "warning" | "destructive" | "not-started";

export interface SteppedProgressStep {
  key: string;
  label: string;
  state: SteppedProgressState;
  successTone?: "success" | "neutral";
  successIcon?: "check" | "warning";
  disabled?: boolean;
  disabledReason?: string;
}

export interface SteppedProgressProps {
  steps: SteppedProgressStep[];
  currentIndex?: number;
  interactive?: boolean;
  compact?: boolean;
  onSelect?: (index: number) => void;
  ariaLabel?: string;
  className?: string;
}

export const steppedProgressStateLabel: Record<SteppedProgressState, string> = {
  success: "Complete",
  "in-progress": "In progress",
  warning: "Needs attention",
  destructive: "Error",
  "not-started": "Not started",
};

const StepStateIcon = ({
  state,
  successTone,
  successIcon,
}: {
  state: SteppedProgressState;
  successTone?: SteppedProgressStep["successTone"];
  successIcon?: SteppedProgressStep["successIcon"];
}) => {
  if (state === "success" && successIcon === "warning")
    return <TriangleAlert aria-hidden="true" size={12} strokeWidth={2.5} />;
  if (state === "success" && successTone === "neutral")
    return (
      <Info
        aria-hidden="true"
        size={12}
        strokeWidth={2.5}
      />
    );
  if (state === "success")
    return (
      <Check
        aria-hidden="true"
        size={12}
        strokeWidth={3}
      />
    );
  if (state === "in-progress")
    return (
      <Circle
        aria-hidden="true"
        fill="currentColor"
        size={10}
      />
    );
  if (state === "warning")
    return (
      <TriangleAlert
        aria-hidden="true"
        size={12}
        strokeWidth={2.5}
      />
    );
  if (state === "destructive")
    return (
      <CircleAlert
        aria-hidden="true"
        size={12}
        strokeWidth={2.5}
      />
    );
  return null;
};

const DefaultSteppedProgress = ({
  steps,
  currentIndex = -1,
  interactive = true,
  compact = false,
  onSelect,
  ariaLabel = "Progress steps",
  className,
}: SteppedProgressProps) => {
  const listRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const list = listRef.current;
    const item = currentIndex >= 0 ? (list?.children.item(currentIndex) as HTMLElement | null) : null;
    if (!list || !item) return;

    const itemStart = item.offsetLeft;
    const itemEnd = itemStart + item.offsetWidth;
    const visibleStart = list.scrollLeft;
    const visibleEnd = visibleStart + list.clientWidth;
    if (itemStart >= visibleStart && itemEnd <= visibleEnd) return;

    const left = itemStart < visibleStart ? itemStart : Math.max(0, itemEnd - list.clientWidth);
    if (typeof list.scrollTo === "function") list.scrollTo({ left, behavior: "smooth" });
    else list.scrollLeft = left;
  }, [currentIndex, steps.length]);

  return (
    <nav
      className={clsx(styles.root, className)}
      aria-label={interactive ? ariaLabel : undefined}
      aria-hidden={interactive ? undefined : true}
      data-compact={compact || undefined}
      data-deci-stepped-progress="true"
    >
      <ol
        ref={listRef}
        className={styles.list}
      >
        {steps.map((step, index) => {
          const status = step.state === "success" && step.successIcon === "warning"
            ? "Complete with warnings"
            : steppedProgressStateLabel[step.state];
          const tooltip = (
            <div className={styles.tooltipText}>
              <strong>
                {index + 1}. {step.label}
              </strong>
              <span>
                {step.disabled
                  ? `Unavailable: ${step.disabledReason ?? "Complete the required earlier steps first."}`
                  : status}
              </span>
            </div>
          );
          const sharedProps = {
            className: styles.step,
            "data-success-tone": step.successTone,
            "data-success-icon": step.successIcon,
            "data-current": index === currentIndex || undefined,
            "data-state": step.state,
          };

          if (!interactive) {
            return (
              <li
                key={step.key}
                className={styles.item}
              >
                <span {...sharedProps}>
                  {compact ? null : (
                    <StepStateIcon
                      state={step.state}
                      successTone={step.successTone}
                      successIcon={step.successIcon}
                    />
                  )}
                </span>
              </li>
            );
          }

          return (
            <li
              key={step.key}
              className={styles.item}
            >
              <Tooltip placement="bottom">
                <Tooltip.Trigger asChild>
                  <Pressable
                    {...sharedProps}
                    aria-label={
                      step.disabled
                        ? `${step.label} unavailable: ${step.disabledReason ?? "Complete the required earlier steps first."}`
                        : `Go to ${step.label}: ${status}`
                    }
                    aria-current={index === currentIndex ? "step" : undefined}
                    disabled={step.disabled}
                    onClick={() => onSelect?.(index)}
                    data-focus-ring
                  >
                    <Pressable.Ripple />
                    {compact ? null : (
                      <StepStateIcon
                        state={step.state}
                        successTone={step.successTone}
                        successIcon={step.successIcon}
                      />
                    )}
                  </Pressable>
                </Tooltip.Trigger>
                <Tooltip.Content className={styles.tooltipContent}>{tooltip}</Tooltip.Content>
              </Tooltip>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export const SteppedProgress = createPluggable<SteppedProgressProps>("deci.steppedProgress", DefaultSteppedProgress);

export default SteppedProgress;
